import { useEffect, useState } from "react";
import { Bell, Wifi, WifiOff, Menu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { BrandMark } from "@/components/BrandMark";
import Sidebar from "@/components/layout/Sidebar";
import { listAlerts, botInfo } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export const TopBar = ({ wsConnected }) => {
    const [unackedCount, setUnackedCount] = useState(0);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [bot, setBot] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const refresh = async () => {
            try {
                const data = await listAlerts(true);
                setUnackedCount(data.alerts?.length || 0);
            } catch (_) {}
            try {
                setBot(await botInfo());
            } catch (_) {}
        };
        refresh();
        const t = setInterval(refresh, 15000);
        return () => clearInterval(t);
    }, []);

    return (
        <header
            className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            data-testid="app-topbar"
        >
            <div className="flex h-14 items-center gap-3 px-4 md:px-6">
                {/* Mobile menu */}
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            data-testid="topbar-mobile-menu-button"
                            aria-label="القائمة"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-64 p-0">
                        <Sidebar mobile onNavigate={() => setSheetOpen(false)} />
                    </SheetContent>
                </Sheet>

                <div className="md:hidden">
                    <BrandMark />
                </div>

                <div className="flex-1" />

                {/* Bot status */}
                {bot && (
                    <div
                        className="hidden items-center gap-2 rounded-full border border-border/60 bg-card/40 px-3 py-1 text-xs lg:inline-flex"
                        data-testid="topbar-bot-status"
                    >
                        <span
                            className={
                                bot.status === "running"
                                    ? "live-dot"
                                    : "h-2 w-2 rounded-full bg-rose-500"
                            }
                            style={{
                                backgroundColor:
                                    bot.status === "running"
                                        ? "hsl(188 92% 45%)"
                                        : undefined,
                            }}
                        />
                        <span className="font-mono" dir="ltr">
                            @{bot.username || "bot"}
                        </span>
                    </div>
                )}

                {/* WS Status */}
                <div
                    className="flex items-center gap-2 rounded-full border border-border/60 bg-card/40 px-3 py-1 text-xs"
                    data-testid="topbar-websocket-status"
                >
                    {wsConnected ? (
                        <>
                            <Wifi className="h-3.5 w-3.5 text-emerald-400" />
                            <span className="text-emerald-300">متصل</span>
                        </>
                    ) : (
                        <>
                            <WifiOff className="h-3.5 w-3.5 text-rose-400" />
                            <span className="text-rose-300">غير متصل</span>
                        </>
                    )}
                </div>

                {/* Alerts */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/alerts")}
                    className="relative"
                    data-testid="topbar-alerts-button"
                    aria-label="التنبيهات"
                >
                    <Bell className="h-5 w-5" />
                    {unackedCount > 0 && (
                        <Badge
                            variant="destructive"
                            className="absolute -top-1 -left-1 h-5 min-w-5 px-1 text-[10px]"
                        >
                            {unackedCount}
                        </Badge>
                    )}
                </Button>
            </div>
        </header>
    );
};

export default TopBar;
